import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { CommandPalette } from './CommandPalette';
import { useBackend } from '../lib/useDb';
import { SuiteLauncher } from '../components/SuiteLauncher';
import { SUITE } from '../data/suite';
import '../components/capture.css';
import { AtlasView } from '../modules/Atlas';
import { Arena } from '../modules/Arena';
import { TemplatesView } from '../modules/Templates';
import { CanonView } from '../modules/Canon';
import { ExpensesView } from '../modules/Expenses';
import { RegistryView } from '../modules/Registry';
import { Circus } from '../modules/Circus';
import { SitePage } from '../modules/SitePage';
import { TodayView } from '../modules/Today';
import { SafetyView } from '../modules/Safety';
import { ScribeView } from '../modules/Scribe';
import { LeaveView } from '../modules/Leave';
import { PurchasingView } from '../modules/Purchasing';
import { CongressView } from '../modules/Congress';
import { PipelineView } from '../modules/Pipeline';
import { ChannelView } from '../modules/Channel';
import { StudioView } from '../modules/Studio';
import { LedgerView } from '../modules/Ledger';
import { TradeView } from '../modules/Trade';
import { CustomersView } from '../modules/Customers';
import { SupportView } from '../modules/Support';
import { ProjectsView } from '../modules/Projects';
import { ForecastView } from '../modules/Forecast';
import { CouncilView } from '../modules/Council';
import { BankView } from '../modules/Bank';
import { DocketView } from '../modules/Docket';
import { ContractsView, QualityView, CrewView, ChorusView, GrantsView } from '../modules/Operations';
import { Travel } from '../modules/Travel';
import './shell.css';
import { ErrorNotices } from '../components/ErrorNotices';
import { DemoBanner } from '../components/DemoBanner';
import { TENANT } from '@tenant';
import { DataReviewView } from '../modules/DataReview';
import { EvidenceView } from '../modules/Evidence';
import { MarketProvider } from '../components/MarketBar';

export interface Brand {
  productName: string;
  orgName: string;
}

/* Module registry. In production this is app.org_modules: a tenant that has not enabled
   Docket never renders it. Nothing here is specific to transplantation or to Scira. */
const MODULES = [
  { key: 'today',     path: '/today',     label: 'Today',     icon: 'sun',      group: 'work', hint: 'Your day: notices, what is waiting on you, the diary and deadlines' },
  // The meeting scribe: capture, minutes, actions, drafted follow-ups. Beside Today because
  // it feeds the same day-to-day loop, not a register somebody audits.
  { key: 'scribe',    path: '/scribe',    label: 'Scribe',    icon: 'file',     group: 'work', hint: 'Meetings in, minutes out: decisions, dated actions and drafted follow-ups' },
  { key: 'projects',  path: '/projects',  label: 'Projects',  icon: 'layers',   group: 'work', hint: 'Plans on a Gantt you drag: phases, dependencies, milestones, baselines and the critical path, assigned across the team' },
  { key: 'atlas',     path: '/atlas',     label: 'Atlas',     icon: 'globe',    group: 'market', hint: 'The market model: {sites}, payers and every route to reimbursement' },
  { key: 'pipeline',  path: '/pipeline',  label: 'Pipeline',  icon: 'funnel',   group: 'market', hint: 'Opportunities on a board, with the four parties to every deal' },
  { key: 'customers', path: '/customers', label: 'Customers', icon: 'people',   group: 'market', hint: 'The customer database: accounts, commercial intel flowing into Atlas, and who holds a portal key' },
  { key: 'support',   path: '/support',   label: 'Support',   icon: 'shield',   group: 'market', hint: 'Customer cases: complaints, non-conformities, feature requests and tech support, escalated through Chorus with the record kept' },
  { key: 'channel',   path: '/channel',   label: 'Channel',   icon: 'share',    group: 'market', hint: 'Distributors and partners, gated by due diligence' },
  { key: 'studio',    path: '/studio',    label: 'Studio',    icon: 'layers',   group: 'market', hint: 'Marketing assets and their medical, legal and regulatory review' },
  // Arena is the go-to-market strategy, Circus is who else is in the market. Both sit under
  // Market because that is what they are about, and both are core Forum rather than tenant
  // features: every customer has competitors and a strategy.
  { key: 'circus',    path: '/circus',    label: 'Circus',    icon: 'share',    group: 'market', hint: 'Who else is in the market, what they sell, and where we stand' },
  { key: 'arena',     path: '/arena',     label: 'Arena',     icon: 'shield',   group: 'market', hint: 'The go-to-market strategy: decisions, moves and what happens next' },
  // The conference calendar: researched by AI with sources, deadlines on the morning sweep,
  // abstracts with their outcomes.
  { key: 'congress',  path: '/congress',  label: 'Congress',  icon: 'globe',    group: 'market', hint: 'Conferences and conventions: deadlines, submissions, every abstract with its outcome, and the reports written afterwards' },
  { key: 'contracts', path: '/contracts', label: 'Contracts', icon: 'file',     group: 'run', hint: 'Every agreement, its status, its value and its notice periods' },
  // The org's own stationery. Under "Run the company" rather than Market because it is
  // infrastructure everyone uses, not something the commercial team owns.
  { key: 'templates', path: '/templates', label: 'Templates', icon: 'file',     group: 'run', hint: '{org} stationery: letterheads and document templates' },
  // Canon is the body of rules the company is bound by. Under Assure rather than Run, because
  // it is the same kind of obligation as Quality and Docket: proving how we behave is a control.
  { key: 'canon',     path: '/canon',     label: 'Canon',     icon: 'shield',   group: 'assure', hint: 'The company policies: drafted, questioned, approved, signed' },
  { key: 'council',   path: '/council',   label: 'Council',   icon: 'people',   group: 'assure', hint: 'An AI shadow board: independent expert seats, one synthesised view' },
  // Health, safety and wellbeing: the incident register (which is the accident book), the
  // statutory compliance basics, and the policies. Under Assure because it is an obligation
  // with an inspector at the end of it.
  { key: 'safety',    path: '/safety',    label: 'Safety',    icon: 'shield',   group: 'assure', hint: 'Health, safety and wellbeing: incidents, statutory basics, policies' },
  // What the company IS, as opposed to what it follows: constitutional, statutory, executed.
  { key: 'registry',  path: '/registry',  label: 'Registry',  icon: 'file',     group: 'assure', hint: 'What the company IS: statutory, constitutional and executed documents' },
  { key: 'ledger',    path: '/ledger',    label: 'Ledger',    icon: 'coins',    group: 'run', hint: 'Quote to cash and credit control' },
  { key: 'trade',     path: '/trade',     label: 'Trade',     icon: 'share',    group: 'run', hint: 'Quotes, orders, lot-tracked stock and shipments; every lot traceable to its customer' },
  // The plan, not the fact: distributor / direct / profit-share arrangements, growth
  // curves, product-line lifecycle, and people cost that can be pulled live from Crew.
  // Sits beside Ledger because both are money, but this one has not happened yet.
  // Route/schema stay 'forecast'/'finance' (internal, stable); the user-facing name is
  // Glidepath, matching the standalone tool this was ported from.
  { key: 'forecast',  path: '/forecast',  label: 'Glidepath', icon: 'coins',    group: 'run', hint: 'The plan ahead: growth curves, product lines and people cost' },
  // Claim, approve, export. Never pays: payment stays in payroll where the bank controls are.
  { key: 'expenses',  path: '/expenses',  label: 'Expenses',  icon: 'coins',    group: 'run', hint: 'Claim, approve, export to payroll; Forum never pays' },
  // Booking a holiday and recording sickness, with the same approval routing as expenses.
  { key: 'leave',     path: '/leave',     label: 'Leave',     icon: 'sun',      group: 'run', hint: 'Holidays and absence: book it, approve it, see who is away' },
  // Commitment before spend: purchase orders with the expense thresholds and routing.
  { key: 'purchasing', path: '/purchasing', label: 'Purchasing', icon: 'coins',   group: 'run', hint: 'Commitment before spend: purchase orders against your thresholds' },
  // Reads the bank and reconciles cash to invoices so revenue recognises on
  // evidence. Never pays: visibility is a per-account allowlist, not a role.
  { key: 'bank',      path: '/bank',      label: 'Bank',      icon: 'coins',    group: 'run', hint: 'The bank feed, reconciled to invoices and capital' },
  // Request, approve, hand to a booking channel. Never books, never pays: fulfilment is
  // tenant configuration, and the completed trip exports into an expense claim.
  { key: 'travel',    path: '/travel',    label: 'Travel',    icon: 'plane',    group: 'run', hint: 'Trips: request, approve, book, and out the other side into expenses' },
  { key: 'crew',      path: '/crew',      label: 'Crew',      icon: 'people',   group: 'run', hint: 'People, the roles they hold, and the competences those roles require' },
  { key: 'chorus',    path: '/chorus',    label: 'Chorus',    icon: 'chat',     group: 'run', hint: 'Messages and notifications inside the company' },
  { key: 'docket',    path: '/docket',    label: 'Docket',    icon: 'shield',   group: 'assure', hint: 'Patent families and the dated obligations that cannot slip' },
  { key: 'quality',   path: '/quality',   label: 'Quality',   icon: 'check',    group: 'assure', hint: 'Controlled documents, nonconformances and CAPAs' },
  { key: 'grants',    path: '/grants',    label: 'Grants',    icon: 'award',    group: 'assure', hint: 'Funding applications, the calls behind them, and their hard windows' },
  // Sits under Assure because it is the same kind of obligation as Quality and Docket: proving
  // the figures in the model are current is a control, not a research activity.
  { key: 'data',      path: '/data',      label: 'Data',      icon: 'check',    group: 'assure', hint: 'Is what we hold still current? The review register says' },
  // Beside Data because they answer the same question from opposite ends: Data asks whether
  // what we hold is still current, Evidence asks whether it was ever sourced.
  { key: 'evidence',  path: '/evidence',  label: 'Evidence',  icon: 'file',     group: 'assure', hint: 'Every figure with the sentence it came from: sources in, claims out' },
] as const;

const GROUPS: { key: string; label: string }[] = [
  { key: 'work', label: '' },
  { key: 'market', label: 'Market' },
  { key: 'run', label: 'Run the company' },
  { key: 'assure', label: 'Assure' },
];

/* Inline icon set. Kept as stroke paths so they inherit currentColor and stay crisp in
   both themes without shipping an icon font. */
function Icon({ name, size = 15 }: { name: string; size?: number }) {
  const p: Record<string, JSX.Element> = {
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" /></>,
    funnel: <path d="M3 5h18l-7 8v6l-4 2v-8z" />,
    share: <><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8.3 10.9l7.4-3.7M8.3 13.1l7.4 3.7" /></>,
    layers: <><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></>,
    file: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>,
    coins: <><ellipse cx="12" cy="6.5" rx="7" ry="3" /><path d="M5 6.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5M5 11.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" /></>,
    people: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5.5a3 3 0 0 1 0 5.8M17 14.2c2.4.7 4 2.9 4 5.8" /></>,
    chat: <path d="M21 12a8 8 0 0 1-8 8H7l-4 3v-6.5A8 8 0 0 1 11 4h2a8 8 0 0 1 8 8z" />,
    shield: <path d="M12 3l8 3v6c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6z" />,
    check: <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.7 2.7L16 9.5" /></>,
    award: <><circle cx="12" cy="9" r="5.5" /><path d="M8.5 13.5L7 22l5-2.6L17 22l-1.5-8.5" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L21 21" /></>,
    moon: <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />,
    bell: <><path d="M18 9a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" /><path d="M13.7 20a2 2 0 0 1-3.4 0" /></>,
    menu: <path d="M4 7h16M4 12h16M4 17h16" />,
    plane: <><path d="M21 3L3 10.3l7.4 2.6L13 21z" /><path d="M21 3L10.4 12.9" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name] ?? p.check}
    </svg>
  );
}

export function Shell({ brand }: { brand: Brand }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [navHint, setNavHint] = useState<{ text: string; top: number } | null>(null);
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute('data-theme') ?? 'auto',
  );
  const navigate = useNavigate();
  const backend = useBackend();
  const [suiteOpen, setSuiteOpen] = useState(false);

  /* The command palette is the primary navigation surface. Once the team learns
     Ctrl-K, clicking through a nav tree largely stops happening. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cycleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : theme === 'light' ? 'auto' : 'dark';
    setTheme(next);
    if (next === 'auto') {
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('forum.theme');
    } else {
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('forum.theme', next);
    }
  }, [theme]);

  return (
    // The market choice is read once at the top and shared, rather than each module
    // fetching its own copy and drifting out of step with the others.
    <MarketProvider>
    <div className="shell">
      <aside className={`nav ${navOpen ? 'open' : ''}`}>
        <div className="nav-brand">
          <svg className="nav-mark" width="26" height="26" viewBox="0 0 100 100" aria-hidden="true">
            <g transform="translate(50,50) rotate(-40)" fill="none" strokeLinecap="round">
              <circle r="40" stroke="currentColor" strokeOpacity="0.22" strokeWidth="6" strokeDasharray="211 40" />
              <circle r="28" stroke="currentColor" strokeOpacity="0.45" strokeWidth="7" strokeDasharray="148 28" />
              <circle r="17" stroke="var(--accent)" strokeWidth="8" strokeDasharray="90 17" />
            </g>
            <circle cx="50" cy="50" r="6.5" fill="var(--accent)" />
          </svg>
          <div className="nav-brand-text">
            <div className="nav-product display">{brand.productName}</div>
            <div className="nav-org">{brand.orgName}</div>
          </div>
        </div>

        {/* One fixed tooltip for the whole nav: the item list scrolls, so anything
            positioned inside it would clip at the panel edge. The hint says what a module
            IS, because an index this long stops being self-explanatory. */}
        {navHint && (
          <div className="nav-hint" style={{ top: navHint.top }} role="tooltip">
            {navHint.text}
          </div>
        )}
        <nav className="nav-items">
          {GROUPS.map((g) => {
            const items = MODULES.filter((m) => m.group === g.key);
            if (!items.length) return null;
            return (
              <div key={g.key} className="nav-group">
                {g.label && <div className="nav-group-label">{g.label}</div>}
                {items.map((m) => (
                  <NavLink
                    key={m.key}
                    to={m.path}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => { setNavOpen(false); setNavHint(null); }}
                    onMouseEnter={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      const text = (('hint' in m ? m.hint : '') as string)
                        .replace('{org}', TENANT.brand.orgName)
                        .replace('{sites}', TENANT.vocabulary.sitePlural.toLowerCase());
                      if (text) setNavHint({ text, top: r.top + r.height / 2 });
                    }}
                    onMouseLeave={() => setNavHint(null)}
                  >
                    <Icon name={m.icon} />
                    <span>{m.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="nav-foot">
          <button className="nav-item" onClick={cycleTheme} title={`Theme: ${theme}`}>
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            <span style={{ textTransform: 'capitalize' }}>{theme}</span>
          </button>
          <div className="nav-user">
            <div className="avatar">DL</div>
            <div className="nav-user-text">
              <div className="nav-user-name">Dr Lawrence</div>
              <div className="nav-user-role">Owner</div>
            </div>
          </div>
        </div>
      </aside>

      {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}

      <div className="main">
        {/* Inside the main column, ABOVE the header, so it is a strip across the top of the
            content and never a flex sibling of it. As a sibling of .main in the shell's row it
            took the remaining width and squeezed the entire application to nothing: the public
            demonstration was showing a full-screen notice and no product. */}
        <DemoBanner />
        <header className="topbar">
          <button className="icon-btn only-mobile" onClick={() => setNavOpen(true)} aria-label="Open navigation">
            <Icon name="menu" size={18} />
          </button>

          <button className="omni" onClick={() => setPaletteOpen(true)}>
            <Icon name="search" size={14} />
            <span>Search or jump to…</span>
            <kbd>{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} K</kbd>
          </button>

          <div className="topbar-right">
            <button className="suite-btn" onClick={() => setSuiteOpen(true)} title="Open another Ariadne application">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <circle cx="6" cy="6" r="2" /><circle cx="12" cy="6" r="2" /><circle cx="18" cy="6" r="2" />
                <circle cx="6" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="18" cy="12" r="2" />
                <circle cx="6" cy="18" r="2" /><circle cx="12" cy="18" r="2" /><circle cx="18" cy="18" r="2" />
              </svg>
              Suite
            </button>
            <SyncBadge {...backend} />
            <button className="icon-btn" aria-label="Notifications">
              <Icon name="bell" size={16} />
              <span className="dot" />
            </button>
          </div>
        </header>

        <main className="content">
          <Routes>
            {/* Everyone lands on Today: notices, the diary and what is coming up. */}
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<TodayView />} />
            <Route path="/atlas/site/:id" element={<SitePage />} />
            <Route path="/atlas/*" element={<AtlasView />} />
            <Route path="/pipeline" element={<PipelineView />} />
            <Route path="/channel" element={<ChannelView />} />
            <Route path="/studio" element={<StudioView />} />
            <Route path="/contracts" element={<ContractsView />} />
            <Route path="/ledger" element={<LedgerView />} />
            <Route path="/trade" element={<TradeView />} />
            <Route path="/customers" element={<CustomersView />} />
            <Route path="/support" element={<SupportView />} />
            <Route path="/projects" element={<ProjectsView />} />
            <Route path="/forecast" element={<ForecastView />} />
            <Route path="/bank" element={<BankView />} />
            <Route path="/crew" element={<CrewView />} />
            <Route path="/chorus" element={<ChorusView />} />
            <Route path="/docket" element={<DocketView />} />
            <Route path="/quality" element={<QualityView />} />
            <Route path="/grants" element={<GrantsView />} />
            <Route path="/data" element={<DataReviewView />} />
            <Route path="/circus" element={<Circus />} />
            <Route path="/templates" element={<TemplatesView />} />
            <Route path="/canon" element={<CanonView />} />
            <Route path="/council" element={<CouncilView />} />
            <Route path="/safety" element={<SafetyView />} />
            <Route path="/scribe" element={<ScribeView />} />
            <Route path="/leave" element={<LeaveView />} />
            <Route path="/purchasing" element={<PurchasingView />} />
            <Route path="/congress" element={<CongressView />} />
            <Route path="/expenses" element={<ExpensesView />} />
            <Route path="/travel" element={<Travel />} />
            <Route path="/registry" element={<RegistryView />} />
            <Route path="/arena" element={<Arena />} />
            <Route path="/evidence" element={<EvidenceView />} />
          </Routes>
        </main>
      </div>

      {suiteOpen && <SuiteLauncher onClose={() => setSuiteOpen(false)} />}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={(p) => {
          if (p.startsWith('suite:')) {
            const app = SUITE.find((a) => a.key === p.slice(6));
            setPaletteOpen(false);
            // Restricted apps must go through the step-up gate, never straight from the palette.
            if (app?.tier === 'step_up') { setSuiteOpen(true); return; }
            if (app) window.open(app.url, '_blank', 'noopener,noreferrer');
            return;
          }
          navigate(p);
          setPaletteOpen(false);
        }}
        modules={[
          ...MODULES.map((m) => ({ label: m.label, path: m.path })),
          ...SUITE.map((a) => ({ label: a.name, path: `suite:${a.key}` })),
        ]}
      />
      <ErrorNotices />
    </div>
    </MarketProvider>
  );
}

function SyncBadge({ backend, online, pending, sync }:
  { backend: string; online: boolean; pending: number; sync: () => void }) {
  if (!online) {
    return (
      <span className="sync offline" title="Offline. Writes are held locally and sync on reconnect.">
        <span className="sync-dot" /> Offline{pending ? ` · ${pending} held` : ''}
      </span>
    );
  }
  if (pending) {
    return (
      <button className="sync pending" onClick={sync}
        title={backend === 'local' ? 'No backend configured. Click to retry once Supabase is set up.' : 'Click to sync now'}>
        <span className="sync-dot" /> {pending} unsynced
      </button>
    );
  }
  return (
    <span className="sync" title={backend === 'local' ? 'Running on seed data. Set VITE_SUPABASE_URL to connect Postgres.' : 'Connected to Supabase'}>
      <span className="sync-dot" /> {backend === 'local' ? 'Seed data' : 'Live'}
    </span>
  );
}

export { Icon };
